import {HttpClient, HttpParams} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {AuditEntry, AuditQuery, ResolveNamesRequest, ResolvedNames} from '../shared/models/audit.model';

/**
 * Talks to the audit read API + the monolith name resolver, both through the gateway off a single BASE_URL (the
 * AuthInterceptor attaches the context token). The audit query lives on the Auditing microservice
 * (`/api/audit/...`, routed by the gateway); name resolution is a monolith endpoint (`/audit/resolve-names`, the
 * gateway's default route) because the identity stores live there, not in the audit service.
 *
 * Both are gated server-side to the audit-oversight roles (PlatformOwner / CompanyOwner / Auditor); a 403
 * surfaces if the caller lacks them.
 */
@Injectable({providedIn: 'root'})
export class AuditHttpService {
  /** Owned by the Auditing service's controller routes — the gateway routes the `/api/audit` prefix verbatim. */
  private static readonly Entries = '/api/audit/v2025.1/entries';
  /** Monolith resolver (default gateway route — NOT under `/api/audit`, which is the audit microservice). */
  private static readonly ResolveNames = '/audit/resolve-names';

  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  /** Lists audit entries, newest first, with optional filters. Company-scoped server-side for non-platform callers. */
  list(query: AuditQuery = {}): Observable<AuditEntry[]> {
    let params = new HttpParams();
    if (query.category) params = params.set('category', query.category);
    if (query.targetType) params = params.set('targetType', query.targetType);
    if (query.targetId) params = params.set('targetId', query.targetId);
    if (query.from) params = params.set('from', query.from);
    if (query.to) params = params.set('to', query.to);
    if (query.take != null) params = params.set('take', String(query.take));
    return this.http.get<AuditEntry[]>(this.baseUrl + AuditHttpService.Entries, {params});
  }

  /** Resolves user / company / shard ids to display names (monolith owns the identity stores). */
  resolveNames(request: ResolveNamesRequest): Observable<ResolvedNames> {
    return this.http.post<ResolvedNames>(this.baseUrl + AuditHttpService.ResolveNames, request);
  }
}
